import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { linkHost } from 'fym-shared/src/smartlinks/linkLabel';
import { TypedMemo } from '../../../common/types';
import { catalogLinksMessages as m } from './catalogLinksMessages';
import { CatalogLinksServiceRow } from './CatalogLinksServiceRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './CatalogLinks.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogLinksFoundServicesProps {
  /** The block's own order of the services. */
  services: readonly string[];
  /** The live links of the song, keyed by `MusicAPIIntegrationID`. */
  links: Record<string, string>;
}

/**
 * "Services, in page order" after the release (design c2): the found services first (the block's
 * order, then the new ones), then the services not found yet. Read only: Music24 finds the links.
 */
const CatalogLinksFoundServicesComponent: React.FC<CatalogLinksFoundServicesProps> = ({ services, links }) => {
  const intl = useIntl();
  const found = useMemo(() => {
    const keys = Object.keys(links);
    return [...services.filter((one) => keys.includes(one)), ...keys.filter((one) => !services.includes(one))];
  }, [links, services]);
  const missing = useMemo(
    () =>
      [...services, ...defaultPresaveServices].filter(
        (one, index, all) => !found.includes(one) && all.indexOf(one) === index
      ),
    [found, services]
  );

  return (
    <div className={styles.field}>
      <span className={styles.label}>
        {intl.formatMessage(m.services)}
        <span className={styles.labelEnd}>
          {intl.formatMessage(m.servicesFound, { found: found.length, missing: missing.length })}
        </span>
      </span>
      <div className={styles.list}>
        {found.map((service) => (
          <CatalogLinksServiceRow
            key={service}
            service={service}
            sub={linkHost(links[service]) ?? links[service] ?? null}
            chip={intl.formatMessage(m.found)}
            chipTone="ok"
          />
        ))}
        {missing.map((service) => (
          <CatalogLinksServiceRow
            key={service}
            service={service}
            sub={null}
            chip={intl.formatMessage(m.notFound)}
            chipTone="wait"
          />
        ))}
      </div>
      <p className={styles.help}>{intl.formatMessage(m.servicesFoundHelp)}</p>
    </div>
  );
};

export const CatalogLinksFoundServices = TypedMemo(CatalogLinksFoundServicesComponent);
